import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Typography from '@tiptap/extension-typography'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import { Markdown } from 'tiptap-markdown'
import { Addition, Deletion, Note, parseAnnotations, serializeAnnotations } from '@/components/editor/AnnotationExtension'
import { EditorToolbar } from '@/components/editor/EditorToolbar'
import { TagInput } from '@/components/ui/TagInput'
import { useIngestStore } from '@/stores/useIngestStore'
import { submitIngestion, subscribeToWorkflow, getSessionManagerOutput } from '@/lib/api'
import type { WorkflowMessage, CardOutput } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Upload, Loader2, Check, AlertCircle, Link2, Sparkles, Plus } from 'lucide-react'

type PageState = 'input' | 'processing' | 'results'

export function IngestPage() {
  const navigate = useNavigate()
  const [pageState, setPageState] = useState<PageState>('input')
  const [extractedCards, setExtractedCards] = useState<CardOutput[]>([])
  const [stats, setStats] = useState({ cardsNew: 0, cardsMerged: 0, edgesCreated: 0 })

  const {
    content,
    setContent,
    title,
    setTitle,
    tags,
    setTags,
    filename,
    setFilename,
    isProcessing,
    nodeProgress,
    taskStatus,
    error,
    startProcessing,
    updateTaskStatus,
    updateNodeProgress,
    setError,
    reset,
  } = useIngestStore()

  const wsRef = useRef<WebSocket | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Tiptap editor configuration - matching zinsser-app
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
      }),
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Placeholder.configure({
        placeholder: 'Paste your content here...',
      }),
      Link.configure({
        openOnClick: false,
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      Table.configure({
        resizable: false,
      }),
      TableRow,
      TableHeader,
      TableCell,
      Addition,
      Deletion,
      Note,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose-zinsser outline-none min-h-full',
      },
    },
    onUpdate: ({ editor }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const storage = editor.storage as any
      if (storage?.markdown?.getMarkdown) {
        const markdown = storage.markdown.getMarkdown()
        // Convert HTML annotation marks back to markdown format
        const serialized = serializeAnnotations(markdown)
        setContent(serialized)
      }
    },
    immediatelyRender: false,
  })

  // Update editor when content changes externally (e.g., file upload)
  const isUpdatingRef = useRef(false)

  useEffect(() => {
    if (!editor || isUpdatingRef.current) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = editor.storage as any
    const currentContent = storage?.markdown?.getMarkdown?.() || ''

    if (content !== currentContent) {
      isUpdatingRef.current = true
      if (content) {
        // Parse annotations before setting content
        const parsedContent = parseAnnotations(content)
        editor.commands.setContent(parsedContent)
      } else {
        editor.commands.clearContent()
      }
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 10)
    }
  }, [editor, content])

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [])

  // Handle completed workflow
  useEffect(() => {
    if (taskStatus?.status === 'COMPLETED' && taskStatus.result) {
      const output = getSessionManagerOutput(taskStatus.result)
      if (output) {
        setExtractedCards(output.cards)
        setStats({
          cardsNew: output.cards_new,
          cardsMerged: output.cards_merged,
          edgesCreated: output.edges_created,
        })
        setPageState('results')
      }
    } else if (taskStatus?.status === 'FAILED') {
      setPageState('input')
    }
  }, [taskStatus])

  const handleSubmit = useCallback(async () => {
    if (!content.trim()) {
      setError('Please enter some content to process')
      return
    }

    try {
      setPageState('processing')

      const response = await submitIngestion({
        raw_text: content,
        title: title.trim() || undefined,
        filename: filename || undefined,
        tags: tags.length > 0 ? tags : undefined,
      })

      startProcessing(response)

      // Close any existing WebSocket
      if (wsRef.current) {
        wsRef.current.close()
      }

      // Subscribe to WebSocket for real-time progress
      wsRef.current = subscribeToWorkflow(
        response.task_id,
        (message: WorkflowMessage) => {
          if (message.type === 'node_start' && message.node) {
            updateNodeProgress(message.node as any, 'running')
          } else if (message.type === 'node_complete' && message.node) {
            updateNodeProgress(message.node as any, 'completed', message.output)
          } else if (message.type === 'workflow_complete') {
            const result = (message as any).result
            if (result) {
              updateTaskStatus({
                task_id: response.task_id,
                status: 'COMPLETED',
                result: result,
              })
              wsRef.current?.close()
            }
          } else if (message.type === 'workflow_error') {
            updateTaskStatus({
              task_id: response.task_id,
              status: 'FAILED',
              error: message.error,
            })
            wsRef.current?.close()
          }
        },
        () => {
          setError('Lost connection to server')
          setPageState('input')
        }
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit')
      setPageState('input')
    }
  }, [content, title, tags, filename, startProcessing, updateTaskStatus, updateNodeProgress, setError])

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      setFilename(file.name)

      const reader = new FileReader()
      reader.onload = (event) => {
        const text = event.target?.result as string
        setContent(text)
        // Update editor with file content
        if (editor) {
          editor.commands.setContent(text)
        }
      }
      reader.readAsText(file)
    },
    [setContent, setFilename, editor]
  )

  const handleInsertMore = useCallback(() => {
    reset()
    setExtractedCards([])
    setPageState('input')
    // Clear editor
    if (editor) {
      editor.commands.clearContent()
    }
  }, [reset, editor])

  const handleCardClick = useCallback((cardId: string) => {
    navigate(`/cards/${cardId}`)
  }, [navigate])

  // Render based on page state
  if (pageState === 'processing') {
    return <ProcessingView nodeProgress={nodeProgress} />
  }

  if (pageState === 'results') {
    return (
      <ResultsView
        cards={extractedCards}
        stats={stats}
        onInsertMore={handleInsertMore}
        onCardClick={handleCardClick}
      />
    )
  }

  // Input state - Following Allied Works centered layout with generous whitespace
  return (
    <div className="relative flex flex-col h-full overflow-hidden">
      {/* Page header with editable title and tags */}
      <div className="flex-shrink-0 bg-background">
        <div className="max-w-6xl mx-auto px-6 md:px-8 pt-6 md:pt-8 pb-4 space-y-2">
          <div className="flex items-center gap-3">
            {/* Editable title input */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled"
              className={cn(
                'bg-transparent border-none outline-none flex-1 min-w-0',
                'text-xl font-light',
                'text-foreground placeholder:text-muted-foreground'
              )}
            />
            {/* New/refresh button */}
            <button
              onClick={handleInsertMore}
              className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              title="New source"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          {/* Tags input */}
          <TagInput
            tags={tags}
            onTagsChange={setTags}
            placeholder="#tags"
          />
        </div>
      </div>

      {/* Scrollable editor area - extra bottom padding on mobile for fixed toolbar */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain cursor-text"
        onClick={() => editor?.commands.focus()}
      >
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 md:py-12 pb-[50vh] md:pb-[40vh] min-h-full">
          <div className="max-w-3xl">
            <EditorContent editor={editor} className="min-h-full" />

            {/* Error message */}
            {error && (
              <p className="text-sm text-destructive mt-6">{error}</p>
            )}
          </div>
        </div>
      </div>

      {/* Editor toolbar - fixed on mobile, relative on desktop */}
      {/* actionBarHeight: py-4 (32px) + button height (~40px) = ~72px */}
      <EditorToolbar editor={editor} disabled={isProcessing} actionBarHeight={72} />

      {/* Bottom action bar */}
      <div className="flex-shrink-0 bg-background border-t border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4">
          <div className="flex items-center gap-3">
            {/* Upload button - secondary style */}
            <label className="cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                accept=".txt,.md,.markdown"
                className="hidden"
                disabled={isProcessing}
              />
              <span
                className={cn(
                  'inline-flex items-center gap-2',
                  'px-4 py-2.5 text-sm font-light',
                  'border border-solid border-[hsl(0_0%_90%)] rounded-md',
                  'text-[hsl(0_0%_12%)] hover:bg-[hsl(0_0%_97%)]',
                  'transition-colors cursor-pointer',
                  isProcessing && 'opacity-50 cursor-not-allowed'
                )}
              >
                <Upload className="w-4 h-4" strokeWidth={1.5} />
                Upload
              </span>
            </label>

            {/* Extract button - primary style */}
            <button
              onClick={handleSubmit}
              disabled={isProcessing || !content.trim() || !title.trim()}
              className={cn(
                'inline-flex items-center gap-2',
                'px-4 py-2.5 text-sm font-light',
                'bg-[hsl(0_0%_12%)] text-white rounded-md',
                'hover:bg-[hsl(0_0%_20%)]',
                'transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  Processing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  Extract Cards
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Processing View Component
interface ProcessingViewProps {
  nodeProgress: Array<{
    node: string
    status: 'pending' | 'running' | 'completed' | 'failed'
  }>
}

function ProcessingView({ nodeProgress }: ProcessingViewProps) {
  const nodeLabels: Record<string, string> = {
    IngestNode: 'Ingesting content',
    CardifyNode: 'Extracting cards',
    EmbedNode: 'Generating embeddings',
    CanonicalizeNode: 'Finding duplicates',
    LinkNode: 'Creating links',
    UpdateStatsNode: 'Updating statistics',
    ReviewQueueNode: 'Queueing reviews',
    SessionManagerNode: 'Finalizing',
  }

  const completedCount = nodeProgress.filter(n => n.status === 'completed').length
  const totalCount = nodeProgress.length

  return (
    <div className="h-full flex items-center justify-center px-6 md:px-8">
      <div className="w-full max-w-md">
        <p className="label-uppercase mb-8 text-center">Processing</p>

        {/* Progress indicator */}
        <p className="text-sm text-muted-foreground text-center mb-12">
          {completedCount} of {totalCount} steps complete
        </p>

        {/* Node list - tight internal spacing */}
        <div className="space-y-2">
          {nodeProgress.map(({ node, status }) => (
            <div
              key={node}
              className={cn(
                'flex items-center gap-3 py-2',
                status === 'pending' && 'opacity-40'
              )}
            >
              {/* Status indicator */}
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                {status === 'running' && (
                  <Loader2 className="w-4 h-4 animate-spin text-foreground" strokeWidth={1.5} />
                )}
                {status === 'completed' && (
                  <Check className="w-4 h-4 text-foreground" strokeWidth={1.5} />
                )}
                {status === 'failed' && (
                  <AlertCircle className="w-4 h-4 text-destructive" strokeWidth={1.5} />
                )}
                {status === 'pending' && (
                  <div className="w-1.5 h-1.5 rounded-full bg-border" />
                )}
              </div>

              {/* Label */}
              <span className={cn(
                'text-sm font-light',
                status === 'running' && 'text-foreground',
                status === 'completed' && 'text-muted-foreground',
                status === 'pending' && 'text-muted-foreground',
                status === 'failed' && 'text-destructive'
              )}>
                {nodeLabels[node] || node}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Results View Component (Allied Works Grid)
interface ResultsViewProps {
  cards: CardOutput[]
  stats: { cardsNew: number; cardsMerged: number; edgesCreated: number }
  onInsertMore: () => void
  onCardClick: (cardId: string) => void
}

function ResultsView({ cards, stats, onInsertMore, onCardClick }: ResultsViewProps) {
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Results header - aligned with app header */}
      <div className="flex-shrink-0 bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-8 pt-6 md:pt-8 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="label-uppercase mb-1">Extraction Complete</p>
              <p className="text-sm text-muted-foreground">
                {stats.cardsNew} new, {stats.cardsMerged} merged, {stats.edgesCreated} links
              </p>
            </div>
            <button
              onClick={onInsertMore}
              className={cn(
                'inline-flex items-center gap-2',
                'px-4 py-2.5 text-sm font-light',
                'border border-border rounded-md',
                'text-foreground hover:bg-muted/50',
                'transition-colors'
              )}
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Insert More
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable card grid - aligned with header */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
            {cards.map((card) => (
              <CardPreview
                key={card.card_id}
                card={card}
                onClick={() => onCardClick(card.card_id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Card Preview Component - Allied Works portrait style
interface CardPreviewProps {
  card: CardOutput
  onClick: () => void
}

function CardPreview({ card, onClick }: CardPreviewProps) {
  return (
    <button
      onClick={onClick}
      className="text-left w-full group"
    >
      {/* Card content - generous bottom margin */}
      <div className="mb-6">
        <p className="text-sm leading-[1.75] text-foreground line-clamp-6 group-hover:text-foreground/70 transition-colors">
          {card.summary}
        </p>
      </div>

      {/* Card metadata - tight spacing */}
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{card.type}</span>
          {card.is_new && (
            <span className="text-[10px] font-light tracking-wide uppercase text-muted-foreground">
              New
            </span>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>{card.domain}</span>
          </div>
          {card.edges_count > 0 && (
            <span className="flex items-center gap-1">
              <Link2 className="w-3 h-3" strokeWidth={1.5} />
              {card.edges_count}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}
